import { show, hide } from '../common';

class ActiveFilterValue {
    private _paramId: number;

    get paramId(): number {
        return this._paramId;
    }

    private _value: FilterActiveValue;
    get value(): FilterActiveValue {
        return this._value;
    }

    private clearFunction: (value: any) => void;

    private _element: HTMLElement;
    get element(): HTMLElement {
        return this._element;
    }

    constructor(paramId: number, value: FilterActiveValue, clearFunction: (value: any) => void) {
        this._paramId = paramId;
        this._value = value;
        this.clearFunction = clearFunction;
    }

    public render(): HTMLElement {
        if (this.element)
            return this.element;

        const element = document.createElement('li');
        element.className = 'filters-summary__item';

        const link = document.createElement('a');
        link.className = 'filters-summary__item-label';
        link.innerHTML = this._value.displayName;
        link.href = '#';
        link.addEventListener('click', event => this.onClearClick(event));
        element.appendChild(link);

        this._element = element;
        return this.element;
    }

    private onClearClick(event: MouseEvent): any {
        event.preventDefault();
        this.clearFunction(this._value.value);
    }
}

export class FilterActiveValues {
    paramId: number;
    values: FilterActiveValue[];
    clearFunction: (value: any) => void;

    constructor(paramId: number, values: FilterActiveValue[], clearFunction: (value: any) => void) {
        this.paramId = paramId;
        this.values = values;
        this.clearFunction = clearFunction;
    }
}

export interface FilterActiveValue {
    value: any;
    displayName: string;
}

export default class ActiveFilters {
    private baseElement: HTMLElement;
    private itemListElement: HTMLUListElement;
    private clearAllFiltersLink: HTMLAnchorElement;
    private _activeFilters: ActiveFilterValue[];

    public get activeFilters(){
        return this._activeFilters.slice();
    }

    clearAllFunction: () => void;

    constructor(clearAllFunction: () => void) {
        this._activeFilters = [];
        this.baseElement = document.querySelector('[data-component="ActiveFilters"]');
        if (!this.baseElement)
            return;

        this.clearAllFunction = clearAllFunction;
        this.itemListElement = this.baseElement.querySelector('[data-component-part="selected-values-list"]');
        this.clearAllFiltersLink = this.baseElement.querySelector('[data-component-part="clear-all-filters-link"]');
        this.clearAllFiltersLink.addEventListener('click', (evt: Event) => this.onClearAllClick(evt));
        this.refreshComponentPartsVisibility();
    }

    setActiveValues(filterActiveValues: FilterActiveValues) {
        if (!this.baseElement)
            return; 

        const filterValuesToRemove = this.getFilterValuesForRemove(filterActiveValues.paramId, filterActiveValues.values);
        const filterValuesToAdd = this.getFilterValuesToAdd(filterActiveValues);
        const elementAfter = this.removeAll(filterValuesToRemove);
        this.insertAllBefore(filterValuesToAdd, elementAfter);
        this.refreshComponentPartsVisibility();
    }

    private onClearAllClick(evt: Event): any {
        evt.preventDefault();
        this.clearAllFunction();
    }

    public clearActiveValues(paramId: number) {
        if (!this.baseElement)
            return; 

        const filterValueForParam = this.getValues(paramId);
        this.removeAll(filterValueForParam);
        this.refreshComponentPartsVisibility();
    }

    private getValues(paramId: number) {
        const values = this._activeFilters.filter(v => v.paramId === paramId);
        return values;
    }

    private removeAll(valuesToRemove: ActiveFilterValue[]): Element {
        let elementAfter: Element;

        const values = this._activeFilters
            .filter(x => valuesToRemove.some(v => v.paramId == x.paramId && v.value.value === x.value.value));

        values.forEach(v => {
            elementAfter = v.element.nextElementSibling;
            this.itemListElement.removeChild(v.element);
            const index = this._activeFilters.indexOf(v);
            if (index < 0)
                throw 'value not found in array';

            this._activeFilters.splice(index, 1);
        });
        return elementAfter;
    }

    private insertAllBefore(values: ActiveFilterValue[], beforeElement: Element): void {
        if (!beforeElement)
            beforeElement = this.clearAllFiltersLink.parentElement;

        values.forEach(v => {
            this.itemListElement.insertBefore(v.render(), beforeElement);
            this._activeFilters.push(v);
        });
    }

    private getFilterValuesToAdd(filterActiveValues: FilterActiveValues): ActiveFilterValue[] {
        const currentActiveValuesForFilter = this._activeFilters
            .filter(v => v.paramId == filterActiveValues.paramId)
            .map(v => v.value.value);

        const filtersToAdd = filterActiveValues.values
            .filter(v => !this.arrayContains(currentActiveValuesForFilter, v.value));

        return filtersToAdd.map(v => new ActiveFilterValue(filterActiveValues.paramId, v, (value) => filterActiveValues.clearFunction(value)));
    }

    private arrayContains(array: any[], value: any) {
        const result = array.some(x => x === value);
        return result;
    }

    private getFilterValuesForRemove(paramId: number, newValues: FilterActiveValue[]): ActiveFilterValue[] {
        const filtersForRemove = this._activeFilters.filter(v => v.paramId === paramId && !newValues.some(nv => nv.value === v.value.value));
        return filtersForRemove;
    }

    private refreshComponentPartsVisibility(){
        this.refreshItemListVisibility();
        this.refreshRemoveAllLinkVisibility();
    }

    private refreshItemListVisibility() {
        if(this._activeFilters.length > 0){
            show(this.itemListElement);
        } else {
            hide(this.itemListElement);
        }
    }

    private refreshRemoveAllLinkVisibility(){
        if(this._activeFilters.length > 1){
            show(this.clearAllFiltersLink);
        } else {
            hide(this.clearAllFiltersLink);
        }
    }
}
